/**
 * CopyEmbedButton
 *
 * Copies a diagram's Embed_Snippet (Markdown that shows the latest committed
 * Diagram_File as an image linking back to the editor) to the clipboard and
 * confirms with a toast (Requirement 11.1). The snippet is built from the
 * `RepoInfo` the API returns and the app's own origin for the current
 * environment, so no project path or app URL is hard-coded (Requirement 11.2,
 * 11.3).
 *
 * Shared between the Diagram_Browser's actions menu (task 5.4) and the
 * Diagram_Editor header (task 6.3), so the embed format lives in one place.
 */

import { useState } from "react"
import { toast } from "sonner"
import { Check, Link2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  buildEmbedSnippet,
  type DiagramSummary,
  type RepoInfo,
} from "@/services/diagramsService"

/** The props for {@link CopyEmbedButton}. */
export interface CopyEmbedButtonProps {
  /** The diagram to embed (for its id, title, and path). */
  diagram: DiagramSummary
  /** The repo info (web URL and branch), for the raw image URL. */
  repo: RepoInfo
  /** The button's visual variant; defaults to "outline". */
  variant?: React.ComponentProps<typeof Button>["variant"]
  /** The button's size; defaults to "sm". */
  size?: React.ComponentProps<typeof Button>["size"]
}

/** How long the copied-state checkmark shows, in milliseconds. */
const COPIED_RESET_MS: number = 1500

/**
 * A button that copies the diagram's Embed_Snippet to the clipboard.
 *
 * @param props - See {@link CopyEmbedButtonProps}.
 * @returns The rendered copy-embed button.
 */
export default function CopyEmbedButton({
  diagram,
  repo,
  variant = "outline",
  size = "sm",
}: CopyEmbedButtonProps): React.JSX.Element {
  const [copied, setCopied] = useState<boolean>(false)

  /**
   * Build the Embed_Snippet and write it to the clipboard, toasting on success
   * or failure. The app base URL is the current origin, which is the deployed
   * app URL in dev and prod. Fails loudly (a toast) rather than silently when
   * the clipboard API is unavailable (e.g. an insecure context).
   */
  async function handleCopy(): Promise<void> {
    const snippet: string = buildEmbedSnippet({
      diagram,
      repo,
      appBaseUrl: window.location.origin,
    })
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      toast.success("Embed snippet copied to the clipboard")
      window.setTimeout((): void => setCopied(false), COPIED_RESET_MS)
    } catch (caught: unknown) {
      toast.error(
        caught instanceof Error
          ? `Could not copy the embed snippet: ${caught.message}`
          : "Could not copy the embed snippet",
      )
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={(): void => {
        void handleCopy()
      }}
      aria-label={`Copy embed snippet for ${diagram.title}`}
    >
      {copied ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}
      Copy embed
    </Button>
  )
}
